import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ResultsFilter } from "@/components/ResultsFilter";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Droplets, Truck, Filter, Pencil, ArrowUp, ArrowDown, Printer, FileText, Thermometer, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SupplierTrend } from "@/components/SupplierTrend";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getMilkBatches, createMilkBatch, updateMilkBatch, getReceptionPlans, getSuppliers, fmt, deleteMilkBatch, getCompanySettings, downloadPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";

const NumField = ({ id, label, unit, value, onChange, step = "0.01", testid }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input
        id={id}
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="pr-12 text-right font-mono tabular-nums"
      />
      {unit && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {unit}
        </span>
      )}
    </div>
  </div>
);

const empty = {
  supplier: "",
  supplier_id: "",
  quantity_l: "",
  date: "",
  fat: "",
  protein: "",
  ph: "",
  temperature: "",
  density: "",
  snf: "",
  acidity: "",
  vehicle: "",
  driver: "",
  notes: "",
};

const DEFAULT_MAX_TEMP = 6;

export default function MilkIntake() {
  const { can } = useAuth();
  const { t, lang } = useT();
  const canDelete = can("admin", "manager");
  const canEdit = can("admin", "manager");
  const [batches, setBatches] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [sortDir, setSortDir] = useState("desc"); // sort by pickup date
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [receptionId, setReceptionId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [maxTemp, setMaxTemp] = useState(DEFAULT_MAX_TEMP);
  const [tab, setTab] = useState(() => (searchParams.get("tab") === "suppliers" ? "suppliers" : "receptions"));
  const [printing, setPrinting] = useState(false);

  // Filters: supplier, month (YYYY-MM), specific date (YYYY-MM-DD)
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const load = async () => setBatches(await getMilkBatches());
  useEffect(() => {
    load();
    getSuppliers({ category: "milk" }).then(setSupplierList).catch(() => {});
    // Company-configurable max accepted milk temperature (Settings).
    getCompanySettings()
      .then((cs) => { const v = Number(cs?.milk_max_temp_c); if (v > 0) setMaxTemp(v); })
      .catch(() => {});
  }, []);

  const isOverTemp = (temp) => temp != null && temp !== "" && Number(temp) > maxTemp;

  const suppliers = useMemo(
    () => Array.from(new Set(batches.map((b) => b.supplier).filter(Boolean))).sort(),
    [batches]
  );

  const filtersActive = supplierFilter !== "all" || monthFilter !== "" || dateFilter !== "";
  const clearFilters = () => {
    setSupplierFilter("all");
    setMonthFilter("");
    setDateFilter("");
  };

  const filtered = useMemo(() => {
    const rows = batches.filter((b) => {
      if (supplierFilter !== "all" && b.supplier !== supplierFilter) return false;
      const day = (b.date || b.created_at || "").slice(0, 10);
      if (dateFilter && day !== dateFilter) return false;
      if (monthFilter && day.slice(0, 7) !== monthFilter) return false;
      return true;
    });
    const dayOf = (b) => (b.date || b.created_at || "");
    rows.sort((a, b) => {
      const cmp = dayOf(a).localeCompare(dayOf(b));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [batches, supplierFilter, monthFilter, dateFilter, sortDir]);

  const pager = usePagination(filtered, 50);

  // Total litres + volume-weighted average composition across the currently filtered batches.
  const filteredStats = useMemo(() => {
    let totalL = 0;
    const acc = { fat: { sum: 0, w: 0 }, protein: { sum: 0, w: 0 }, density: { sum: 0, w: 0 } };
    filtered.forEach((b) => {
      const vol = Number(b.quantity_l) || 0;
      totalL += vol;
      const w = vol > 0 ? vol : 1; // fall back to equal weight if volume missing
      ["fat", "protein", "density"].forEach((k) => {
        const v = Number(b.analysis?.[k]);
        if (!Number.isNaN(v) && b.analysis?.[k] !== null && b.analysis?.[k] !== "" && b.analysis?.[k] !== undefined) {
          acc[k].sum += v * w;
          acc[k].w += w;
        }
      });
    });
    const avg = (k) => (acc[k].w > 0 ? acc[k].sum / acc[k].w : null);
    return { totalL, avgFat: avg("fat"), avgProtein: avg("protein"), avgDensity: avg("density") };
  }, [filtered]);
  const filteredTotalL = filteredStats.totalL;

  // Quick action deep link (?add=1) — e.g. from the phone floating button or the home-screen shortcut.
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setEditId(null); setReceptionId(null); setForm(empty); setOpen(true);
      const next = new URLSearchParams(searchParams); next.delete("add");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Prefill from a scheduled reception (?receptionId=...)
  useEffect(() => {
    const rid = searchParams.get("receptionId");
    if (rid) {
      Promise.all([getReceptionPlans(), getSuppliers({ category: "milk" }).catch(() => [])]).then(([rps, sups]) => {
        const rp = rps.find((r) => r.id === rid);
        if (rp) {
          setReceptionId(rp.id);
          const match = (sups || []).find((s) => s.name === rp.supplier);
          setForm((f) => ({
            ...f,
            supplier: rp.supplier || "",
            supplier_id: match ? match.id : "",
            quantity_l: rp.expected_volume_l != null ? String(rp.expected_volume_l) : "",
            fat: rp.expected_fat != null ? String(rp.expected_fat) : "",
          }));
          setOpen(true);
        }
        searchParams.delete("receptionId");
        setSearchParams(searchParams, { replace: true });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const startAdd = () => {
    setEditId(null);
    setReceptionId(null);
    setForm(empty);
    setOpen(true);
  };

  const startEdit = (m) => {
    setReceptionId(null);
    setEditId(m.id);
    setForm({
      supplier: m.supplier || "",
      supplier_id: m.supplier_id || "",
      quantity_l: m.quantity_l != null ? String(m.quantity_l) : "",
      date: (m.date || "").slice(0, 10),
      fat: m.analysis?.fat ?? "",
      protein: m.analysis?.protein ?? "",
      ph: m.analysis?.ph ?? "",
      temperature: m.analysis?.temperature ?? "",
      density: m.analysis?.density ?? "",
      snf: m.analysis?.snf ?? "",
      acidity: m.analysis?.acidity ?? "",
      vehicle: m.vehicle || "",
      driver: m.driver || "",
      notes: m.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.supplier_id || form.quantity_l === "") {
      toast.error(t("milk.supplierQtyRequired"));
      return;
    }
    if (form.date) {
      const yr = new Date(form.date).getFullYear();
      const maxYr = new Date().getFullYear() + 1;
      if (!yr || yr < 2000 || yr > maxYr) {
        toast.error(t("milk.invalidDateYear"));
        return;
      }
      // Milk cannot be received on a future date.
      const todayStr = new Date().toISOString().slice(0, 10);
      if (form.date.slice(0, 10) > todayStr) {
        toast.error(t("milk.futureDateNotAllowed"));
        return;
      }
    }
    const analysis = {
      fat: form.fat === "" ? null : Number(form.fat),
      protein: form.protein === "" ? null : Number(form.protein),
      ph: form.ph === "" ? null : Number(form.ph),
      temperature: form.temperature === "" ? null : Number(form.temperature),
      density: form.density === "" ? null : Number(form.density),
      snf: form.snf === "" ? null : Number(form.snf),
      acidity: form.acidity === "" ? null : Number(form.acidity),
    };
    const supplierName = (supplierList.find((s) => s.id === form.supplier_id) || {}).name || form.supplier;
    setSaving(true);
    try {
      if (editId) {
        await updateMilkBatch(editId, {
          supplier: supplierName,
          supplier_id: form.supplier_id,
          quantity_l: Number(form.quantity_l),
          date: form.date ? new Date(form.date).toISOString() : undefined,
          notes: form.notes,
          vehicle: form.vehicle,
          driver: form.driver,
          analysis,
        });
        toast.success(t("milk.updated"));
      } else {
        await createMilkBatch({
          supplier: supplierName,
          supplier_id: form.supplier_id,
          quantity_l: Number(form.quantity_l),
          date: form.date ? new Date(form.date).toISOString() : undefined,
          notes: form.notes,
          reception_plan_id: receptionId || undefined,
          vehicle: form.vehicle,
          driver: form.driver,
          analysis,
        });
        toast.success(receptionId ? t("milk.receivedClosed") : t("milk.added"));
      }
      setForm(empty);
      setEditId(null);
      setReceptionId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editId ? t("milk.failedUpdate") : t("milk.failedAdd")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await deleteMilkBatch(id);
    toast.success(t("milk.deleted"));
    load();
  };

  // Period report follows the active filters: a specific day, a month, or everything.
  const reportRange = () => {
    if (dateFilter) return { start: dateFilter, end: dateFilter };
    if (monthFilter) {
      const [y, m] = monthFilter.split("-").map(Number);
      const last = new Date(y, m, 0).getDate();
      return { start: `${monthFilter}-01`, end: `${monthFilter}-${String(last).padStart(2, "0")}` };
    }
    return {};
  };

  const printReport = async () => {
    setPrinting(true);
    try {
      const { start, end } = reportRange();
      const params = new URLSearchParams({ lang });
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (supplierFilter !== "all") params.set("supplier", supplierFilter);
      await downloadPdf(`/milk-batches/report?${params.toString()}`, `milk-report_${start || "all"}_${end || "all"}.pdf`);
      toast.success(t("milk.reportReady"));
    } catch {
      toast.error(t("milk.reportFailed"));
    } finally {
      setPrinting(false);
    }
  };

  const printTicket = async (m) => {
    try {
      await downloadPdf(`/milk-batches/${m.id}/report?lang=${lang}`, `milk-reception_${m.batch_code}.pdf`);
    } catch {
      toast.error(t("milk.reportFailed"));
    }
  };

  return (
    <Layout
      actions={
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setReceptionId(null); setEditId(null); setForm(empty); } }}>
          <Button size="sm" variant="outline" className="mr-2" onClick={printReport} disabled={printing || filtered.length === 0} data-testid="milk-print-report">
            <Printer className="mr-1.5 h-4 w-4" /> {printing ? t("milk.reportPreparing") : t("milk.printReport")}
          </Button>
          <Button size="sm" data-testid="add-milk-button" onClick={startAdd}>
            <Plus className="mr-1.5 h-4 w-4" /> {t("milk.addMilk")}
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editId ? t("milk.editReception") : receptionId ? t("milk.receiveScheduled") : t("milk.addReception")}</DialogTitle>
            </DialogHeader>
            {receptionId && !editId && (
              <div className="flex items-center gap-2 rounded-lg bg-[hsl(var(--accent)/0.15)] px-3 py-2 text-sm text-[hsl(var(--brand))]">
                <Truck className="h-4 w-4" /> {t("milk.fulfillingHint")}
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="supplier">{t("milk.supplierReq")}</Label>
                <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id")(v)}>
                  <SelectTrigger id="supplier" data-testid="milk-supplier-select">
                    <SelectValue placeholder={t("milk.selectSupplier")} />
                  </SelectTrigger>
                  <SelectContent>
                    {supplierList.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-muted-foreground" data-testid="milk-no-suppliers">
                        {t("milk.noSuppliers")}
                      </div>
                    ) : (
                      supplierList.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`milk-supplier-option-${s.id}`}>
                          {s.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <NumField id="qty" label={t("milk.quantityReq")} unit="L" step="1" value={form.quantity_l} onChange={set("quantity_l")} testid="milk-quantity-input" />
              <div className="space-y-1.5 sm:col-span-1">
                <Label htmlFor="recdate">{t("milk.pickupDate")}</Label>
                <Input id="recdate" type="date" min="2000-01-01" max={new Date().toISOString().slice(0, 10)} value={form.date} onChange={(e) => set("date")(e.target.value)} data-testid="milk-date-input" />
              </div>
              <NumField id="fat" label={t("dashboard.fat")} unit="%" value={form.fat} onChange={set("fat")} testid="milk-fat-input" />
              <NumField id="protein" label={t("planning.protein")} unit="%" value={form.protein} onChange={set("protein")} testid="milk-protein-input" />
              <NumField id="ph" label="pH" unit="pH" value={form.ph} onChange={set("ph")} testid="milk-ph-input" />
              <div className="space-y-1.5">
                <NumField id="temp" label={t("milk.temperature")} unit="°C" step="0.1" value={form.temperature} onChange={set("temperature")} testid="milk-temp-input" />
                {isOverTemp(form.temperature) ? (
                  <p className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--danger))]" data-testid="milk-temp-warning">
                    <AlertTriangle className="h-3.5 w-3.5" /> {t("milk.tempOverLimit", { max: maxTemp })}
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">{t("milk.tempMaxHint", { max: maxTemp })}</p>
                )}
              </div>
              <NumField id="acidity" label={t("milk.acidity")} unit="°T" step="0.5" value={form.acidity} onChange={set("acidity")} testid="milk-acidity-input" />
              <NumField id="density" label={t("milk.density")} unit="g/cm³" step="0.001" value={form.density} onChange={set("density")} testid="milk-density-input" />
              <NumField id="snf" label="SNF" unit="%" value={form.snf} onChange={set("snf")} testid="milk-snf-input" />
              <div className="space-y-1.5">
                <Label htmlFor="vehicle">{t("milk.vehicle")}</Label>
                <Input id="vehicle" value={form.vehicle} onChange={(e) => set("vehicle")(e.target.value)} placeholder="B-123-ABC" data-testid="milk-vehicle-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="driver">{t("milk.driver")}</Label>
                <Input id="driver" value={form.driver} onChange={(e) => set("driver")(e.target.value)} data-testid="milk-driver-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="notes">{t("common.notes")}</Label>
                <Textarea id="notes" value={form.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="milk-notes-input" rows={2} />
              </div>
            </div>
            {editId && (
              <p className="text-xs text-muted-foreground">
                {t("milk.editHint")}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={submit} disabled={saving} data-testid="milk-submit-button">
                {saving ? t("common.saving") : editId ? t("milk.saveChanges") : t("milk.addBatch")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="milk-tabs">
          <TabsTrigger value="receptions" data-testid="milk-tab-receptions"><Droplets className="mr-1.5 h-4 w-4" /> {t("supplierTrend.tabReceptions")}</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="milk-tab-suppliers"><Users className="mr-1.5 h-4 w-4" /> {t("supplierTrend.tabSuppliers")}</TabsTrigger>
        </TabsList>
        <TabsContent value="suppliers" className="mt-0">
          <SupplierTrend />
        </TabsContent>
        <TabsContent value="receptions" className="mt-0">
      <TooltipProvider delayDuration={150}>
      <Card>
        <CardHeader className="space-y-4">
          <CardTitle className="text-base">
            {t("milk.title")} ({filtered.length}{filtersActive ? ` ${t("prod.ofTotal", { n: batches.length })}` : ""})
          </CardTitle>
          <ResultsFilter
            selectLabel={t("milk.supplier")}
            selectValue={supplierFilter}
            onSelectChange={setSupplierFilter}
            selectOptions={suppliers.map((s) => ({ value: s, label: s }))}
            allLabel={t("milk.allSuppliers")}
            selectTestId="filter-supplier-select"
            monthValue={monthFilter}
            onMonthChange={setMonthFilter}
            monthTestId="filter-month-input"
            dateValue={dateFilter}
            onDateChange={setDateFilter}
            dateTestId="filter-date-input"
            active={filtersActive}
            onClear={clearFilters}
            clearTestId="filter-clear-button"
          />
          {filtered.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border border-[hsl(var(--brand)/0.3)] bg-[hsl(var(--brand)/0.06)] px-4 py-3" data-testid="milk-total-summary">
              <div className="flex items-center gap-3">
                <Droplets className="h-5 w-5 shrink-0 text-[hsl(var(--brand))]" />
                <div>
                  <div className="text-xs text-muted-foreground">
                    {filtersActive ? t("milk.totalFiltered") : t("milk.totalIntake")}
                  </div>
                  <div className="text-xl font-bold tracking-tight text-foreground">
                    {fmt(filteredTotalL)} <span className="text-sm font-semibold text-muted-foreground">L</span>
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {t("milk.acrossBatches", { n: filtered.length })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 border-l border-[hsl(var(--brand)/0.2)] pl-6">
                <AvgStat label={t("milk.avgFat")} value={filteredStats.avgFat} suffix="%" data-testid="milk-avg-fat" />
                <AvgStat label={t("milk.avgProtein")} value={filteredStats.avgProtein} suffix="%" data-testid="milk-avg-protein" />
                <AvgStat label={t("milk.avgDensity")} value={filteredStats.avgDensity} suffix="" decimals={3} data-testid="milk-avg-density" />
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Droplets className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("milk.empty")}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="filter-empty-state">
              <Filter className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("milk.noMatch")}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters} data-testid="filter-empty-clear">
                {t("prod.clearFilters")}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.batch")}</TableHead>
                    <TableHead>{t("milk.supplier")}</TableHead>
                    <TableHead>
                      <button
                        type="button"
                        onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                        className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                        data-testid="milk-sort-pickup-date"
                      >
                        {t("milk.pickupDate")}
                        {sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">{t("milk.intakeL")}</TableHead>
                    <TableHead className="text-right">{t("milk.remainingL")}</TableHead>
                    <TableHead className="text-right">
                      <span className="inline-flex items-center gap-1"><Thermometer className="h-3.5 w-3.5" /> {t("milk.tempC")}</span>
                    </TableHead>
                    <TableHead className="text-right">{t("milk.fatPct")}</TableHead>
                    <TableHead className="text-right">{t("milk.proteinPct")}</TableHead>
                    <TableHead className="text-right">pH</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pager.pageItems.map((m) => (
                    <TableRow key={m.id} data-testid={`milk-row-${m.id}`}>
                      <TableCell className="font-mono text-xs">{m.batch_code}</TableCell>
                      <TableCell>{m.supplier}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{formatDate(m.date || m.created_at, "-")}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(m.quantity_l)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold">{fmt(m.remaining_l)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums" data-testid={`milk-temp-${m.id}`}>
                        {m.analysis?.temperature == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : isOverTemp(m.analysis.temperature) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="badge-bad border-transparent font-mono" data-testid={`milk-temp-over-${m.id}`}>
                                <AlertTriangle className="mr-1 h-3 w-3" /> {fmt(m.analysis.temperature, 1)}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{t("milk.tempOverLimit", { max: maxTemp })}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-[hsl(var(--success))]">{fmt(m.analysis.temperature, 1)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(m.analysis?.fat, 2)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(m.analysis?.protein, 2)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(m.analysis?.ph, 2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => printTicket(m)} data-testid={`milk-ticket-${m.id}`} aria-label={t("milk.printTicket")}>
                                <FileText className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("milk.printTicket")}</TooltipContent>
                          </Tooltip>
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => startEdit(m)} data-testid={`milk-edit-${m.id}`}>
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" onClick={() => remove(m.id)} data-testid={`milk-delete-${m.id}`}>
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                page={pager.page} pageCount={pager.pageCount} pageSize={pager.pageSize}
                total={pager.total} start={pager.start} end={pager.end}
                onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize}
                testid="milk-pagination"
              />
            </div>
          )}
        </CardContent>
      </Card>
      </TooltipProvider>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}


const AvgStat = ({ label, value, suffix = "", decimals = 2, ...rest }) => (
  <div {...rest}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold tabular-nums text-foreground">
      {value == null ? <span className="text-muted-foreground">—</span> : <>{fmt(value, decimals)}{suffix && <span className="ml-0.5 text-xs font-normal text-muted-foreground">{suffix}</span>}</>}
    </div>
  </div>
);
